import type { ApprovalStep } from '@/types/order';

// ─── Fixed procurement steps ───────────────────────────────────────────────
const STEPS = [
  { key: 'submit',    label: '提交订单' },
  { key: 'approved',  label: '审批完成' },
  { key: 'printed',   label: '单据打印' },
  { key: 'shipped',   label: '出库配送' },
  { key: 'received',  label: '收货签收' },
  { key: 'completed', label: '订单完成' },
];

/** 退货订单售后处理进度 */
const BACK_STEPS = [
  { key: 'submit', label: '提交申请' },
  { key: 'check', label: '售后确认' },
  { key: 'exchange', label: '退换货' },
  { key: 'done', label: '售后完成' },
];

function getStepProgress(status: string, printed: boolean) {
  const isRejected = status === 'rejected';
  const approvedDone =
    status === 'approved' || status === 'shipped' || status === 'received' || status === 'completed';
  const shippedDone = status === 'shipped' || status === 'received' || status === 'completed';
  const receivedDone = status === 'received' || status === 'completed';
  const completedDone = status === 'completed';

  const done: Record<string, boolean> = {
    submit: true,
    approved: approvedDone,
    printed,
    shipped: shippedDone,
    received: receivedDone,
    completed: completedDone,
  };

  let currentKey: string | null = null;
  if (!isRejected) {
    if (!approvedDone) currentKey = 'approved';
    else if (!printed && !shippedDone) currentKey = 'printed';
    else if (!shippedDone) currentKey = 'shipped';
    else if (!receivedDone) currentKey = 'received';
    else if (!completedDone) currentKey = 'completed';
  }

  return { done, currentKey, isRejected };
}

/** 退货订单中文 state → 已完成步骤数 */
function getBackCompletedCount(stateLabel: string): number {
  if (stateLabel === '已完成') return 4;
  if (stateLabel === '退货中') return 2;
  return 2;
}

function formatDatetime(iso: string) {
  const d = new Date(String(iso).replace(/-/g, '/'));
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

interface HorizontalStepperProps {
  status: string;
  createdAt: string;
  approvedAt?: string;
  /** 单据打印时间（is_print 且存在 print_time） */
  printedAt?: string;
  shippedAt?: string;
  /** 收货签收时间（receive_time） */
  receivedAt?: string;
  /** 订单完成时间（complete_time） */
  completedAt?: string;
  /** 签收凭证数量 */
  signImageCount?: number;
  /** 点击收货签收 / 凭据入口 */
  onOpenSignVouchers?: () => void;
}

function HorizontalStepper({
  status,
  createdAt,
  approvedAt,
  printedAt,
  shippedAt,
  receivedAt,
  completedAt,
  signImageCount = 0,
  onOpenSignVouchers,
}: HorizontalStepperProps) {
  const { done, currentKey, isRejected } = getStepProgress(status, !!printedAt);
  const hasSignImages = signImageCount > 0;

  const stepTimes: Record<string, string | undefined> = {
    submit: createdAt,
    approved: approvedAt,
    printed: printedAt,
    shipped: shippedAt,
    received: receivedAt,
    completed: completedAt,
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-start min-w-[680px]">
        {STEPS.map((step, index) => {
          const isDone = done[step.key];
          const isCurrent = currentKey === step.key;
          const isCurrentRejected = isRejected && step.key === 'approved';
          const isLast = index === STEPS.length - 1;
          const prevDone = index > 0 && done[STEPS[index - 1].key];
          const nextDone = !isLast && done[STEPS[index + 1].key];
          const stepTime = isDone || isCurrent ? stepTimes[step.key] : undefined;
          const isReceivedStep = step.key === 'received';
          const canOpenVouchers = isReceivedStep && hasSignImages && !!onOpenSignVouchers;

          return (
            <div key={step.key} className="flex-1 flex flex-col items-center relative">
              {/* Step circle + connecting line */}
              <div className="flex items-center w-full">
                {/* Left connector */}
                <div
                  className={`flex-1 h-px transition-colors ${index === 0 ? 'invisible' : isDone && prevDone ? 'bg-accent-500' : 'bg-background-200'}`}
                />

                {/* Circle */}
                <button
                  type="button"
                  disabled={!canOpenVouchers}
                  onClick={() => {
                    if (canOpenVouchers) onOpenSignVouchers?.();
                  }}
                  className={`shrink-0 relative ${canOpenVouchers ? 'cursor-pointer' : 'cursor-default'}`}
                  aria-label={canOpenVouchers ? '查看签收凭证' : undefined}
                >
                  {isDone ? (
                    <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                  ) : isCurrent ? (
                    <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center ring-4 ring-accent-100">
                      <span className="text-xs font-bold text-white leading-none">{index + 1}</span>
                    </div>
                  ) : isCurrentRejected ? (
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center ring-2 ring-red-400"
                      style={{ backgroundColor: '#ed4014' }}
                    >
                      <i className="ri-close-line text-white text-sm" />
                    </div>
                  ) : (
                    <div className="w-7 h-7 rounded-full border-2 border-background-200 bg-white flex items-center justify-center">
                      <span className="text-xs text-foreground-400 font-medium leading-none">{index + 1}</span>
                    </div>
                  )}
                </button>

                {/* Right connector */}
                <div
                  className={`flex-1 h-px transition-colors ${isLast ? 'invisible' : isDone && nextDone ? 'bg-accent-500' : 'bg-background-200'}`}
                />
              </div>

              {/* Label */}
              <div className="mt-2 text-center px-1">
                {canOpenVouchers ? (
                  <button
                    type="button"
                    onClick={() => onOpenSignVouchers?.()}
                    className="text-xs font-medium leading-tight text-foreground-800 hover:text-primary-600 transition-colors"
                  >
                    {step.label}
                  </button>
                ) : (
                  <span
                    className={`text-xs font-medium leading-tight ${
                      isDone || isCurrent
                        ? 'text-foreground-800'
                        : isCurrentRejected
                          ? 'text-red-500'
                          : 'text-foreground-400'
                    }`}
                  >
                    {step.label}
                  </span>
                )}
                {/* Timestamp below label */}
                {stepTime && (
                  <p className="text-[10px] text-foreground-400 mt-0.5 leading-tight whitespace-nowrap">
                    {formatDatetime(stepTime)}
                  </p>
                )}
                {canOpenVouchers && (
                  <button
                    type="button"
                    onClick={() => onOpenSignVouchers?.()}
                    className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-primary-600 hover:text-primary-700 font-medium leading-tight"
                  >
                    <i className="ri-image-line text-[10px]" />
                    有{signImageCount}张凭据
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Backorder after-sale horizontal stepper ───────────────────────────────

interface BackorderStepperProps {
  stateLabel: string;
  createdAt: string;
  completedAt?: string;
}

function BackorderHorizontalStepper({ stateLabel, createdAt, completedAt }: BackorderStepperProps) {
  const completedCount = getBackCompletedCount(stateLabel);
  const stepTimes: Record<number, string | undefined> = {
    0: createdAt,
    1: createdAt,
    2: undefined,
    3: completedAt,
  };
  const stepExtras: Record<number, string | undefined> = {
    2: '线下进行退换货',
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-start min-w-[480px]">
        {BACK_STEPS.map((step, index) => {
          const isDone = index < completedCount;
          const isCurrent = !isDone && index === completedCount;
          const isLast = index === BACK_STEPS.length - 1;
          const stepTime = isDone || isCurrent ? stepTimes[index] : undefined;
          const stepExtra = isDone || isCurrent ? stepExtras[index] : undefined;

          return (
            <div key={step.key} className="flex-1 flex flex-col items-center relative">
              <div className="flex items-center w-full">
                <div
                  className={`flex-1 h-px transition-colors ${
                    index === 0 ? 'invisible' : isDone ? 'bg-accent-500' : 'bg-background-200'
                  }`}
                />
                <div className="shrink-0 relative">
                  {isDone ? (
                    <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                  ) : isCurrent ? (
                    <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center ring-4 ring-accent-100">
                      <span className="text-xs font-bold text-white leading-none">{index + 1}</span>
                    </div>
                  ) : (
                    <div className="w-7 h-7 rounded-full border-2 border-background-200 bg-white flex items-center justify-center">
                      <span className="text-xs text-foreground-400 font-medium leading-none">{index + 1}</span>
                    </div>
                  )}
                </div>
                <div
                  className={`flex-1 h-px transition-colors ${
                    isLast
                      ? 'invisible'
                      : isDone && completedCount > index + 1
                        ? 'bg-accent-500'
                        : 'bg-background-200'
                  }`}
                />
              </div>
              <div className="mt-2 text-center px-1">
                <span
                  className={`text-xs font-medium leading-tight ${
                    isDone || isCurrent ? 'text-foreground-800' : 'text-foreground-400'
                  }`}
                >
                  {step.label}
                </span>
                {stepTime && (
                  <p className="text-[10px] text-foreground-400 mt-0.5 leading-tight whitespace-nowrap">
                    {formatDatetime(stepTime)}
                  </p>
                )}
                {!stepTime && stepExtra && (
                  <p className="text-[10px] text-foreground-400 mt-0.5 leading-tight">{stepExtra}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Vertical approval history ─────────────────────────────────────────────

type EntryStatus = 'approved' | 'rejected' | 'current' | 'pending';

interface HistoryEntry {
  id: string;
  time: string;
  name: string;
  role?: string;
  text: string;
  entryStatus: EntryStatus;
}

// Dot + line visual config per status
const ENTRY_META: Record<EntryStatus, {
  dotClass: string;
  iconClass: string;
  nameColor: string;
  textColor: string;
  badgeClass: string;
  badgeLabel: string;
  solidLine: boolean;
}> = {
  approved: {
    dotClass: 'bg-accent-500',
    iconClass: 'ri-check-line text-white text-[10px]',
    nameColor: 'text-foreground-800',
    textColor: 'text-foreground-600',
    badgeClass: 'bg-accent-50 text-accent-700',
    badgeLabel: '已同意',
    solidLine: true,
  },
  rejected: {
    dotClass: 'bg-red-500',
    iconClass: 'ri-close-line text-white text-[10px]',
    nameColor: 'text-foreground-800',
    textColor: 'text-red-600',
    badgeClass: 'bg-red-50 text-red-600',
    badgeLabel: '已拒绝',
    solidLine: true,
  },
  current: {
    dotClass: 'bg-white border-2 border-accent-500',
    iconClass: 'ri-time-line text-accent-500 text-[10px]',
    nameColor: 'text-foreground-800',
    textColor: 'text-accent-600',
    badgeClass: 'bg-amber-50 text-amber-600',
    badgeLabel: '待审批',
    solidLine: false,
  },
  pending: {
    dotClass: 'bg-white border-2 border-background-300',
    iconClass: 'ri-subtract-line text-foreground-300 text-[10px]',
    nameColor: 'text-foreground-400',
    textColor: 'text-foreground-400',
    badgeClass: 'bg-background-100 text-foreground-400',
    badgeLabel: '未审批',
    solidLine: false,
  },
};

function buildHistory(
  orderPerson: string,
  createdAt: string,
  orderStatus: string,
  approvalSteps: ApprovalStep[],
): HistoryEntry[] {
  const entries: HistoryEntry[] = [];

  entries.push({
    id: 'submit',
    time: formatDatetime(createdAt),
    name: orderPerson,
    text: '提交订单',
    entryStatus: 'approved',
  });

  const isActiveOrder = orderStatus === 'pending' || orderStatus === 'approving';
  let currentMarked = false;

  approvalSteps.forEach((step) => {
    let entryStatus: EntryStatus;
    let text: string;

    if (step.status === 'approved') {
      entryStatus = 'approved';
      const commentPart = step.comment ? `，${step.comment}` : '';
      text = `同意审批${commentPart}`;
    } else if (step.status === 'rejected') {
      entryStatus = 'rejected';
      const commentPart = step.comment ? `，${step.comment}` : '';
      text = `驳回审批${commentPart}`;
    } else {
      // pending step
      if (isActiveOrder && !currentMarked) {
        entryStatus = 'current';
        currentMarked = true;
        text = '等待审批';
      } else {
        entryStatus = 'pending';
        text = '尚未审批';
      }
    }

    entries.push({
      id: `step-${step.level}`,
      time: step.approvedAt ? formatDatetime(step.approvedAt) : '',
      name: step.approverName,
      role: step.role,
      text,
      entryStatus,
    });
  });

  return entries;
}

interface ApprovalHistoryProps {
  orderPerson: string;
  createdAt: string;
  orderStatus: string;
  approvalSteps: ApprovalStep[];
}

function ApprovalHistory({ orderPerson, createdAt, orderStatus, approvalSteps }: ApprovalHistoryProps) {
  const entries = buildHistory(orderPerson, createdAt, orderStatus, approvalSteps);

  if (entries.length === 0) return null;

  return (
    <div>
      {entries.map((entry, index) => {
        const isLast = index === entries.length - 1;
        const meta = ENTRY_META[entry.entryStatus];
        return (
          <div key={entry.id} className={`relative flex gap-3 ${isLast ? '' : 'pb-5'}`}>
            {/* Dot column */}
            <div className="relative flex flex-col items-center shrink-0 w-5">
              {/* Circle */}
              <div className={`relative z-10 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${meta.dotClass}`}>
                <i className={meta.iconClass} />
              </div>
              {/* Connector line */}
              {!isLast && (
                meta.solidLine
                  ? <div className="absolute top-5 bottom-0 left-1/2 -translate-x-1/2 w-px bg-background-200" />
                  : <div className="absolute top-5 bottom-0 left-1/2 -translate-x-[0.5px] border-l border-dashed border-background-300" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 pt-0.5">
              {/* Time + badge row */}
              <div className="flex items-center gap-2 mb-0.5">
                {entry.time && (
                  <span className="text-xs text-foreground-400 leading-tight">{entry.time}</span>
                )}
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.badgeClass}`}>
                  {entry.entryStatus === 'current' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-1 animate-pulse" />
                  )}
                  {meta.badgeLabel}
                </span>
              </div>
              {/* Name + role */}
              <p className={`text-sm leading-snug ${meta.nameColor}`}>
                <span className="font-medium">{entry.name}</span>
                {entry.role && (
                  <span className="text-foreground-400 text-xs ml-1.5">{entry.role}</span>
                )}
              </p>
              {/* Action text */}
              <p className={`text-xs mt-0.5 ${meta.textColor}`}>{entry.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main export ───────────────────────────────────────────────────────────

interface OrderProgressPanelProps {
  status: string;
  /** 接口原始中文状态，退货进度用 */
  stateLabel?: string;
  /** 是否退货订单 */
  isBackorder?: boolean;
  orderPerson: string;
  createdAt: string;
  approvedAt?: string;
  /** 单据打印时间（is_print 且存在 print_time） */
  printedAt?: string;
  shippedAt?: string;
  /** 收货签收时间（receive_time） */
  receivedAt?: string;
  /** 订单完成时间（complete_time） */
  completedAt?: string;
  approvalSteps: ApprovalStep[];
  /** 签收凭证数量 */
  signImageCount?: number;
  /** 打开签收凭证弹窗 */
  onOpenSignVouchers?: () => void;
}

export default function OrderProgressPanel({
  status,
  stateLabel = '',
  isBackorder = false,
  orderPerson,
  createdAt,
  approvedAt,
  printedAt,
  shippedAt,
  receivedAt,
  completedAt,
  approvalSteps,
  signImageCount = 0,
  onOpenSignVouchers,
}: OrderProgressPanelProps) {
  if (isBackorder) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-5">售后处理进度</h3>
        <BackorderHorizontalStepper
          stateLabel={stateLabel}
          createdAt={createdAt}
          completedAt={completedAt}
        />
      </div>
    );
  }

  const hasHistory = approvalSteps.some((s) => s.status !== 'pending') || true; // always show submit

  return (
    <div className="bg-white rounded-xl border border-background-200 p-6">
      {/* Section title */}
      <h3 className="text-sm font-semibold text-foreground-900 mb-5">订单进度</h3>

      {/* ① 横向采购进度 */}
      <HorizontalStepper
        status={status}
        createdAt={createdAt}
        approvedAt={approvedAt}
        printedAt={printedAt}
        shippedAt={shippedAt}
        receivedAt={receivedAt}
        completedAt={completedAt}
        signImageCount={signImageCount}
        onOpenSignVouchers={onOpenSignVouchers}
      />

      {/* ② 垂直审批进度 */}
      {hasHistory && (
        <>
          <div className="border-t border-background-100 my-5" />
          <div className="mb-3">
            <span className="text-xs font-medium text-foreground-500 tracking-wide">审批进度</span>
          </div>
          <ApprovalHistory
            orderPerson={orderPerson}
            createdAt={createdAt}
            orderStatus={status}
            approvalSteps={approvalSteps}
          />
        </>
      )}
    </div>
  );
}